import { assert, debounceFunction, ITimeSource, TimeSourceUnsubscribe } from '@fe/core';
import dayjs from 'dayjs';
import { Evented } from 'maplibre-gl';
import {
    WeatherAnimationSettings,
    WeatherSettings,
    WeatherTimelineRange,
    WeatherTimelineSyncMode,
    WeatherTimelineTimeMode
} from '../settings/settings';
import { SelectedWeatherItem } from './types';
import { WeatherLayerPlugin } from './weather-layer-plugin';

export type WeatherTimelineEvents = {
    'timeline.window-changed': { type: 'timeline.window-changed' };
    'timeline.time-now-changed': { type: 'timeline.time-now-changed' };
    'timeline.selected-time-changed': { type: 'timeline.selected-time-changed' };
};

export class WeatherTimeline extends Evented<WeatherTimelineEvents> {
    private selectedTime: number = 0;
    private animationLoopInterval: number | null = null;
    private animationCycleTimeout: number | null = null;
    private animationRunning = false;
    private timeSubscription: TimeSourceUnsubscribe | null = null;
    private timeRangeMode: Exclude<WeatherTimelineRange, WeatherTimelineRange.auto> = WeatherTimelineRange.historyShort;
    private settings: WeatherAnimationSettings | null = null;
    private products: SelectedWeatherItem[] = [];
    private availableTimesSorted: number[] = [];
    private window = {
        min: 0,
        max: 0,
        spanHours: 1
    };
    private snapToFrames = true;
    private autoAdvance = true;
    private updateDebounce = debounceFunction(25);

    constructor(
        private readonly plugin: WeatherLayerPlugin,
        private readonly time: ITimeSource
    ) {
        super();
        this.setEventedParent(plugin as Evented);
        this.selectedTime = this.time.currentTime;
    }

    disableSnapToFrames() {
        this.snapToFrames = false;
    }

    enableSnapToFrames() {
        this.snapToFrames = true;
    }

    disableAutoAdvance() {
        this.autoAdvance = false;
    }

    enableAutoAdvance() {
        this.autoAdvance = true;
    }

    startAutoUpdate() {
        this.updateWindow();
        this.fire('timeline.time-now-changed');
        this.timeSubscription = this.time.on((now, sourceChanged) => {
            // Update every 60s
            if (now % 60 && !sourceChanged) {
                return;
            }
            this.updateWindow();
            this.updateVisibleFrames();
            this.fire('timeline.time-now-changed');
        });
    }

    stopAutoUpdate() {
        this.timeSubscription?.();
        this.timeSubscription = null;
    }

    updateProducts(products: SelectedWeatherItem[], options: { autoMoveToLatestSlide: boolean }) {
        this.products = products.filter((x) => x.loaded && x.success && x.weatherStore);
        this.availableTimesSorted = this.products
            .filter((x) => x.visible)
            .map((x) => x.weatherStore!.frames)
            .flat()
            .map((x) => x.times.validFrom)
            .filter((x) => x !== null)
            .sort();

        this.updateDebounce(() => {
            this.updateWindow();
            this.updateVisibleFrames();
            this.restartAnimation();
            if (!this.animationRunning) {
                if (options.autoMoveToLatestSlide || !this.availableTimesSorted.includes(this.selectedTime)) {
                    this.moveToMostCurrentSlide();
                }
            }
        });
    }

    updateSettings(settings: WeatherSettings) {
        this.settings = settings.animation;
        this.updateWindow();
        this.updateVisibleFrames();
        this.restartAnimation();
    }

    startAnimation() {
        assert(this.settings);
        this.animationRunning = true;
        const frames = this.computeAnimationFrames();
        if (frames.empty) {
            return;
        }

        const slideTimeMs = this.settings.timeToShowSlideSeconds * 1000;
        const cycleTimeMs = this.settings.pauseBetweenCyclesSeconds * 1000;

        const animationLoop = () => {
            let next = frames.next();
            if (next === 'cycle') {
                if (cycleTimeMs !== 0) {
                    self.clearInterval(this.animationLoopInterval!);
                    this.animationLoopInterval = null;
                    this.animationCycleTimeout = self.setTimeout(() => {
                        this.animationLoopInterval = self.setInterval(animationLoop, slideTimeMs);
                        this.animationCycleTimeout = null;
                    }, cycleTimeMs);
                    return;
                } else {
                    next = frames.next();
                }
            }

            // If we don't have a cycle pause this should roll over to the next frame, but in case there's no animation frames available at all check this here
            if (next === 'cycle') {
                return;
            }
            this.setSelectedTimeWithoutSnap(next);
        };

        this.animationLoopInterval = self.setInterval(animationLoop, slideTimeMs);
    }

    stopAnimation() {
        this.animationRunning = false;
        if (this.animationLoopInterval) {
            self.clearInterval(this.animationLoopInterval);
            this.animationLoopInterval = null;
        }
        if (this.animationCycleTimeout) {
            self.clearTimeout(this.animationCycleTimeout);
            this.animationCycleTimeout = null;
        }
    }

    setSelectedTime(selectedTime: number) {
        if (!this.snapToFrames) {
            this.setSelectedTimeWithoutSnap(selectedTime);
            return;
        }

        // Nothing to do here
        if (this.availableTimesSorted.length === 0) {
            return;
        }

        let newSelectedTime: number = this.availableTimesSorted[0];
        for (let i = 0; i < this.availableTimesSorted.length; i++) {
            if (this.availableTimesSorted[i] > selectedTime) {
                break;
            }
            newSelectedTime = this.availableTimesSorted[i];
        }

        if (newSelectedTime === this.selectedTime) {
            return;
        }

        this.selectedTime = newSelectedTime;
        this.updateVisibleFrames();
        this.fire('timeline.selected-time-changed');
    }

    getWindow() {
        return { ...this.window, timelineMode: this.timeRangeMode };
    }

    getTimeNow() {
        return this.time.currentTime;
    }

    getSelectedTime() {
        return this.selectedTime;
    }

    private setSelectedTimeWithoutSnap(selectedTime: number) {
        this.selectedTime = selectedTime;
        this.updateVisibleFrames();
        this.fire('timeline.selected-time-changed');
    }

    private moveToMostCurrentSlide() {
        if (!this.autoAdvance) {
            return;
        }

        let maxPastFrameTime = 0;
        let minFutureFrameTime = Number.MAX_SAFE_INTEGER;
        for (const product of this.products.filter((x) => x.visible)) {
            maxPastFrameTime = product.weatherStore!.frames.reduce(
                (p, n) =>
                    n.times.validFrom && n.times.validFrom <= this.time.currentTime && n.times.validFrom > p ? n.times.validFrom : p,
                maxPastFrameTime
            );

            minFutureFrameTime = product.weatherStore!.frames.reduce(
                (p, n) => (n.times.validFrom && n.times.validFrom > this.time.currentTime && n.times.validFrom < p ? n.times.validFrom : p),
                minFutureFrameTime
            );
        }

        if (maxPastFrameTime !== 0) {
            this.setSelectedTimeWithoutSnap(maxPastFrameTime);
            return;
        }

        if (minFutureFrameTime !== Number.MAX_SAFE_INTEGER) {
            this.setSelectedTimeWithoutSnap(minFutureFrameTime);
            return;
        }
    }

    private restartAnimation() {
        if (this.animationRunning) {
            this.stopAnimation();
            this.startAnimation();
        }
    }

    private updateVisibleFrames() {
        for (const product of this.products) {
            if (!product.weatherStore) {
                continue;
            }

            const frames = product.weatherStore.frames;
            let visFrame = 0;
            let i = 0;
            for (const frame of frames) {
                if (frame.times.validFrom && frame.times.validFrom > this.selectedTime) {
                    break;
                }
                visFrame = i++;
            }
            this.plugin.setFrameVisible(product, visFrame);
        }
    }

    private updateWindow() {
        assert(this.settings);
        this.updateTimeRange();
        const range = animationRangeValues[this.timeRangeMode];
        const absolute = this.settings.timelineTimeMode === WeatherTimelineTimeMode.absolute;
        const now = absolute
            ? dayjs.unix(this.time.currentTime).set('minutes', 0).set('seconds', 0).set('milliseconds', 0).unix()
            : this.time.currentTime;
        this.window.min = now + range.min * 3600;
        this.window.max = now + range.max * 3600;
        this.window.spanHours = range.max - range.min;
        this.fire('timeline.window-changed');
    }

    private updateTimeRange() {
        assert(this.settings);
        if (this.settings.timelineTimeRange !== WeatherTimelineRange.auto) {
            this.timeRangeMode = this.settings.timelineTimeRange;
            return;
        }

        // Auto
        let min: number | null = null;
        let max: number | null = null;

        for (const item of this.products) {
            if (item.weatherStore!.frames.length === 0 || !item.visible) {
                continue;
            }
            const prodMin = item.weatherStore!.frames[0].times.validFrom;
            const prodMax = item.weatherStore!.frames[item.weatherStore!.frames.length - 1].times.validFrom;
            if (!min || (prodMin && prodMin < min)) {
                min = prodMin;
            }

            if (!max || (prodMax && prodMax > max)) {
                max = prodMax;
            }
        }

        if (!min && !max) {
            // Default to short, if no products
            this.timeRangeMode = WeatherTimelineRange.historyShort;
            return;
        }

        min ??= this.time.currentTime;
        max ??= this.time.currentTime;

        const hoursMin = (min - this.time.currentTime) / 3600;
        const hoursMax = (max - this.time.currentTime) / 3600;

        const maxCoverageRageType = getMaxCoverageRange(hoursMin, hoursMax);
        this.timeRangeMode = maxCoverageRageType;
    }

    private computeAnimationFrames(): { next: () => number | 'cycle'; empty: boolean } {
        const syncLayer = this.findSyncLayer();
        let times: number[] = [];
        if (syncLayer) {
            times = syncLayer.weatherStore!.frames.map((x) => x.times.validFrom).filter(Boolean) as number[];
        } else {
            times = this.products
                .filter((x) => x.visible)
                .map((x) => x.weatherStore?.frames)
                .flat()
                .map((x) => x?.times.validFrom)
                .filter(Boolean) as number[];
        }

        times.sort((a, b) => (a - b > 0 ? 1 : -1));
        const numOfSlides = this.settings?.numberOfSlides ?? 16;
        // Take only a selected number of slides starting from the right of the array
        times = times.slice(Math.max(0, times.length - numOfSlides), times.length);
        let i = 0;
        return {
            next: () => {
                if (i >= times.length) {
                    i = 0;
                    return 'cycle';
                }
                return times[i++];
            },
            empty: times.length === 0
        };
    }

    private findSyncLayer(): SelectedWeatherItem | null {
        // TODO: this should only take into account frames that fall within current animation window
        const eligibleProducts = this.products.filter((x) => x.visible);

        if (eligibleProducts.length === 0) {
            return null;
        }
        const mode = this.settings?.timelineSyncMode;
        if (mode === WeatherTimelineSyncMode.bottomMost) {
            return eligibleProducts.find((x) => x.product.animate) ?? null;
        }

        if (mode === WeatherTimelineSyncMode.topMost) {
            return eligibleProducts.findLast((x) => x.product.animate) ?? null;
        }

        if (mode === WeatherTimelineSyncMode.mostFrequent) {
            let syncLayer: SelectedWeatherItem | null = null;
            for (const item of eligibleProducts) {
                if (!item.product.animate || !item.weatherStore) {
                    continue;
                }

                if (item.weatherStore.frames.length > (syncLayer?.weatherStore!.frames.length ?? 0)) {
                    syncLayer = item;
                }
            }
            return syncLayer;
        }

        if (mode === WeatherTimelineSyncMode.custom) {
            const customLayer = eligibleProducts.find((x) => x.id === this.settings?.timelineSyncCustomProduct);
            if (customLayer?.product.animate) {
                return customLayer;
            }
            return null;
        }
        if (mode === WeatherTimelineSyncMode.auto) {
            const animatable = eligibleProducts.filter((x) => x.product.animate);
            animatable.sort(
                (a, b) =>
                    (a.product.updatePeriodMinutes ?? 9999) - (b.product.updatePeriodMinutes ?? 9999) ||
                    a.product.category - b.product.category
            );
            return animatable[0] ?? null;
        }

        return null;
    }
}

export const animationRangeValues: Record<
    Exclude<WeatherTimelineRange, WeatherTimelineRange.auto>,
    { min: number; max: number; smallTickMinutes: number; mediumTickMinutes: number; bigTickMinutes: number }
> = {
    [WeatherTimelineRange.historyShort]: { min: -2, max: 2, smallTickMinutes: 5, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.historyMid]: { min: -4, max: 2, smallTickMinutes: 5, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.historyLong]: { min: -6, max: 2, smallTickMinutes: 5, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.forecastShort]: { min: -2, max: 6, smallTickMinutes: 5, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.forecastMid]: { min: -2, max: 12, smallTickMinutes: 0, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.forecastLong]: { min: -2, max: 24, smallTickMinutes: 0, mediumTickMinutes: 15, bigTickMinutes: 30 },
    [WeatherTimelineRange.full]: { min: -6, max: 24, smallTickMinutes: 0, mediumTickMinutes: 15, bigTickMinutes: 30 }
};

const animationRangesList = Object.entries(animationRangeValues).map((x) => ({
    type: +x[0],
    range: x[1]
}));

function calculateCoverage(start: number, end: number, window: { min: number; max: number }): number {
    const width = end - start;
    if (width === 0) {
        return 0;
    }

    const coveredWidth = Math.min(end, window.max) - Math.max(start, window.min);

    return coveredWidth / width;
}

function getMaxCoverageRange(hoursMin: number, hoursMax: number): Exclude<WeatherTimelineRange, WeatherTimelineRange.auto> {
    let maxCoverageRange = animationRangesList[0];
    let maxCoverage = 0;

    for (const range of animationRangesList) {
        const coverage = calculateCoverage(hoursMin, hoursMax, range.range);
        if (coverage > maxCoverage) {
            maxCoverage = coverage;
            maxCoverageRange = range;
        }
    }
    return maxCoverageRange.type;
}
